import { ReduxService } from 'dashboard-services/services'

export default (() => {
  const EMPTY_FILE_CONFIG = {
    mandatoryColumns: [],
    optionalColumns: [],
    fileType: undefined
  }

  const EMPTY_DYNAMIC_VALIDATION = {
    taskInput: {
      taskName: undefined,
      taskVersion: undefined,
      taskGroupName: undefined,
      params: {}
    },
    continueIfTaskNotFound: false
  }

  const APPROVAL_TEMPLATE_KEYS = [
    { key: "pullRequest", label: "Pending Approval notification template pipeline" },
    { key: "approved", label: "Approve notification template pipeline" },
    { key: "declined", label: "Decline notification template pipeline" },
    { key: "requestedChanges", label: "Request Change notification template pipeline" }
  ]

  const DEFAULT_APPROVAL_ACTIONS = {
    upload: true,
    update: false,
    delete: false
  }

  const EMPTY_TEMPLATES = Object.fromEntries(APPROVAL_TEMPLATE_KEYS.map(({ key }) => [key, null]))

  // The dialog edits one shared approval form; the file stores it as one draftConfigs entry per file type
  const normalizeApproval = ({ draftConfigs = [] } = {}) => {
    const [{ actions, reviewers, pipeline = null, templates } = {}] = draftConfigs,
          normalizedTemplates = ReduxService.updateObject(EMPTY_TEMPLATES, templates)
    return {
      isApprovalEnabled: draftConfigs.length > 0,
      fileTypes: draftConfigs.map(({ fileType }) => fileType),
      actions: ReduxService.updateObject(DEFAULT_APPROVAL_ACTIONS, actions),
      reviewers: (reviewers?.roles || []).map(egId => ({ egId })),
      users: reviewers?.users || [],
      useCustomNotification: !!pipeline || Object.values(normalizedTemplates).some(Boolean),
      pipeline,
      templates: normalizedTemplates
    }
  }

  const toDraftConfigs = ({ isApprovalEnabled, fileTypes = [], actions, reviewers = [], users = [], useCustomNotification, pipeline, templates }) => isApprovalEnabled
    ? fileTypes.map(fileType => ({
      fileType,
      actions,
      reviewers: { users, roles: reviewers.map(({ egId }) => egId) },
      pipeline: useCustomNotification ? pipeline || null : null,
      templates: useCustomNotification ? ReduxService.updateObject(EMPTY_TEMPLATES, templates) : EMPTY_TEMPLATES
    }))
    : []

  const EMPTY_GROUP_CONFIG = {
    isValidationEnabled: false,
    isDynamicValidationEnabled: false,
    symbolsConfig: {
      doNotAllowCreatingNewSymbols: false,
      doNotAllowCreatingNewColumns: false
    },
    filesConfigs: [],
    approval: normalizeApproval()
  }

  const columnsToList = columns => Object.entries(columns || {}).map(([name, values]) => ({ name, values }))

  const columnsToObject = columns => Object.fromEntries((columns || []).map(({ name, values }) => [name, values]))

  const normalizeGroupConfig = (groupConfig = {}) => ReduxService.updateObject(groupConfig, {
    isDynamicValidationEnabled: groupConfig.isDynamicValidationEnabled === undefined ? true : groupConfig.isDynamicValidationEnabled,
    isValidationEnabled: groupConfig.isValidationEnabled === undefined ? true : groupConfig.isValidationEnabled,
    symbolsConfig: {
      doNotAllowCreatingNewColumns: groupConfig.symbolsConfig?.doNotAllowCreatingNewColumns === undefined ? false : groupConfig.symbolsConfig?.doNotAllowCreatingNewColumns,
      doNotAllowCreatingNewSymbols: groupConfig.symbolsConfig?.doNotAllowCreatingNewSymbols === undefined ? false : groupConfig.symbolsConfig?.doNotAllowCreatingNewSymbols
    },
    filesConfigs: (groupConfig.filesConfigs || []).map(fileConfig => ReduxService.updateObject(fileConfig, {
      mandatoryColumns: columnsToList(fileConfig.mandatoryColumns),
      optionalColumns: columnsToList(fileConfig.optionalColumns)
    })),
    approval: normalizeApproval({ draftConfigs: groupConfig.draftConfigs })
  })

  const prepareGroupConfigForUpload = ({ approval = normalizeApproval(), ...groupConfig }) => ReduxService.updateObject(groupConfig, {
    filesConfigs: (groupConfig.filesConfigs || []).map(fileConfig => ReduxService.updateObject(fileConfig, {
      mandatoryColumns: columnsToObject(fileConfig.mandatoryColumns),
      optionalColumns: columnsToObject(fileConfig.optionalColumns)
    })),
    draftConfigs: toDraftConfigs(approval)
  })

  const cleanDynamicValidationParams = ({ dynamicValidation, invalidParamNames, shouldRemoveInvalidParams }) => {
    if(!shouldRemoveInvalidParams) {
      return dynamicValidation
    }

    return ReduxService.updateObject(dynamicValidation, {
      taskInput: ReduxService.updateObject(dynamicValidation.taskInput, {
        params: Object.fromEntries(Object.entries(dynamicValidation?.taskInput?.params || {}).filter(([key]) => !invalidParamNames.includes(key)))
      })
    })
  }

  const getSelectedFileTypes = (filesConfigs = []) => [...new Set(filesConfigs.map(({ fileType }) => fileType).filter(Boolean))]

  const setSelectedFileTypes = ({ filesConfigs = [], fileTypes = [] }) => {
    const keptFilesConfigs = filesConfigs.filter(({ fileType }) => fileTypes.includes(fileType)),
          keptFileTypes = getSelectedFileTypes(keptFilesConfigs),
          newFilesConfigs = fileTypes
            .filter(fileType => !keptFileTypes.includes(fileType))
            .map(fileType => ReduxService.updateObject(EMPTY_FILE_CONFIG, { fileType }))
    return keptFilesConfigs.concat(newFilesConfigs)
  }

  const getFirstFileConfigIndex = ({ filesConfigs = [], fileType }) => filesConfigs.findIndex(fileConfig => fileConfig.fileType === fileType)

  return {
    EMPTY_FILE_CONFIG,
    EMPTY_GROUP_CONFIG,
    EMPTY_DYNAMIC_VALIDATION,
    APPROVAL_TEMPLATE_KEYS,
    normalizeApproval,
    toDraftConfigs,
    normalizeGroupConfig,
    prepareGroupConfigForUpload,
    cleanDynamicValidationParams,
    getSelectedFileTypes,
    setSelectedFileTypes,
    getFirstFileConfigIndex
  }
})()
